import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import api, { imgUrl } from "@/lib/api";
import SEOHead from "@/components/SEOHead";

export function BlogList() {
  const [blogs, setBlogs] = useState([]);
  useEffect(() => { api.get("/blogs").then(({ data }) => setBlogs(data)); }, []);
  return (
    <div data-testid="blog-page" className="bg-[var(--pz-ink)] text-white">
      <SEOHead title="Blog — The Arena" description="Match recaps, league news and stories from the indoor cricket arena." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-xs uppercase tracking-[0.35em] text-[var(--pz-red)] font-bold mb-4">From the arena</div>
        <h1 className="font-display text-5xl md:text-7xl font-bold uppercase leading-none">Blog & News</h1>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {blogs.map((b, i) => (
            <Link
              to={`/blog/${b.slug}`}
              key={b.id}
              data-testid={`blog-card-${i}`}
              className="bg-[var(--pz-ink-2)] border border-[var(--pz-line)] rounded-md overflow-hidden hover:-translate-y-1 transition-all group pz-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              {b.cover_image && (
                <div className="overflow-hidden">
                  <img src={imgUrl(b.cover_image)} alt={b.title} className="w-full h-52 object-cover transition duration-700 group-hover:scale-105" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  <span className="text-[var(--pz-red)] font-bold">{b.category}</span>
                  <span>·</span>
                  <span>{new Date(b.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                </div>
                <h3 className="font-display text-xl font-bold uppercase mt-2 leading-tight text-white group-hover:text-[var(--pz-red)] transition">{b.title}</h3>
                <p className="text-zinc-400 text-sm mt-2 line-clamp-3">{b.excerpt}</p>
              </div>
            </Link>
          ))}
          {blogs.length === 0 && <div className="text-zinc-500">No posts yet.</div>}
        </div>
      </div>
    </div>
  );
}

export function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  useEffect(() => { api.get(`/blogs/${slug}`).then(({ data }) => setPost(data)).catch(() => setPost(false)); }, [slug]);

  if (post === null) return <div className="bg-[var(--pz-ink)] py-32 text-center text-zinc-500">Loading…</div>;
  if (post === false) return <div className="bg-[var(--pz-ink)] py-32 text-center text-zinc-500">Post not found.</div>;

  return (
    <article data-testid="blog-detail-page" className="bg-[var(--pz-ink)] text-white">
      <SEOHead title={`${post.title} — The Arena`} description={post.excerpt} ogImage={imgUrl(post.cover_image)} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-[0.2em] font-bold mb-8"><ArrowLeft className="h-3.5 w-3.5" /> Back to blog</Link>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          <span className="text-[var(--pz-red)] font-bold">{post.category}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(post.created_at).toLocaleDateString()}</span>
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-bold uppercase mt-4 leading-none">{post.title}</h1>
        {post.cover_image && <img src={imgUrl(post.cover_image)} alt={post.title} className="mt-10 w-full aspect-[16/9] object-cover rounded-md" />}
        <div className="prose-pz mt-10" dangerouslySetInnerHTML={{ __html: post.content }} />
      </div>
    </article>
  );
}
